// Per-record diagnosis for DMARC auth_results. Translates raw
// `auth_results.spf` / `auth_results.dkim` rows from a report into
// labeled outcomes the operator can act on:
//
//   - "alignment failure"      → auth passed on a domain that doesn't
//                                share an organizational domain with
//                                the header From. Common for forwards
//                                and ESP envelopes (mailgun, sendgrid).
//                                Fix is *DKIM* alignment, not SPF.
//   - "authentication failure" → SPF/DKIM signature failed at the
//                                claimed domain. The IP/key is genuinely
//                                not authorised — this is a real config
//                                or spoofing signal.
//   - "transient (temperror)"  → receiver had a DNS hiccup. Not your
//                                problem; ignore unless persistent.
//   - "misconfig (permerror)"  → SPF >10 lookups, syntax error, or
//                                broken DKIM key. Real config issue
//                                you control.
//
// We diagnose each auth row in isolation. A single record can have
// several SPF/DKIM rows; the UI renders all of them.

import { isSameOrganization } from "./validator/parents.js";

export type AuthDiagnosisKind =
  | "aligned-pass"
  | "unaligned-pass"
  | "auth-failure"
  | "softfail"
  | "temperror"
  | "permerror"
  | "neutral"
  | "none"
  | "unknown";

export type AuthSeverity = "ok" | "info" | "warn" | "error";

export interface AuthDiagnosis {
  kind: AuthDiagnosisKind;
  severity: AuthSeverity;
  // Short human-readable label for a pill / chip.
  label: string;
  // Longer explanation for tooltip/popover. Tells the operator what
  // it means and what to do (or that there's nothing to do).
  hint: string;
}

// SPF-specific. The auth row's `domain` is compared against
// `headerFrom` (header `From:`) to decide alignment. We ignore the
// `aspf` mode published in the policy because:
//   1. Most reports use relaxed alignment;
//   2. If even relaxed doesn't match, strict won't either, so the
//      diagnosis is the same.
export function diagnoseSpfAuth(
  row: { domain: string | null | undefined; result: string },
  headerFrom: string | undefined,
): AuthDiagnosis {
  const result = (row.result ?? "").toLowerCase();
  const domain = row.domain ?? "";

  switch (result) {
    case "pass": {
      const aligned = headerFrom && domain
        ? isSameOrganization(domain, headerFrom)
        : false;
      if (aligned) {
        return {
          kind: "aligned-pass",
          severity: "ok",
          label: "pass · aligned",
          hint: `SPF authenticated on ${domain || "(no domain)"} and aligns with the header From. Counts toward DMARC pass.`,
        };
      }
      return {
        kind: "unaligned-pass",
        severity: "warn",
        label: "pass · alignment failure",
        hint: `SPF authenticated, but on ${domain || "(no domain)"} — that doesn't share an organizational domain with the header From (${headerFrom ?? "?"}). DMARC needs alignment, so this counts as fail unless DKIM rescues it. Typical for forwards or ESPs that envelope-from a vendor domain (e.g. sendgrid.net, mailgun.org).`,
      };
    }
    case "fail":
      return {
        kind: "auth-failure",
        severity: "error",
        label: "fail · not authorised",
        hint: `The sending IP isn't allowed by the SPF record at ${domain || "(no domain)"}. Either the IP is genuinely unauthorized (spoofing or a sender you forgot to include), or the SPF record was changed mid-window.`,
      };
    case "softfail":
      return {
        kind: "softfail",
        severity: "warn",
        label: "softfail",
        hint: `SPF returned softfail at ${domain || "(no domain)"} — the record has \`~all\` and the IP isn't listed. Treated as soft policy by most receivers; tighten to \`-all\` once you trust your include list.`,
      };
    case "temperror":
      return {
        kind: "temperror",
        severity: "info",
        label: "temperror",
        hint: `Transient DNS error at the receiver while validating SPF for ${domain || "(no domain)"}. Not your config — ignore unless it shows up consistently.`,
      };
    case "permerror":
      return {
        kind: "permerror",
        severity: "error",
        label: "permerror",
        hint: `Permanent SPF error at ${domain || "(no domain)"}. Usually means the record exceeds the 10 DNS-lookup limit (RFC 7208 §4.6.4) or has a syntax error. Open the SPF chain inspector for ${domain} to see which include is bloating the lookup count.`,
      };
    case "neutral":
      return {
        kind: "neutral",
        severity: "info",
        label: "neutral",
        hint: `SPF returned neutral at ${domain || "(no domain)"} — the record has \`?all\` or doesn't take a position on this IP. Receivers can't use this for DMARC alignment.`,
      };
    case "none":
      return {
        kind: "none",
        severity: "warn",
        label: "no SPF policy",
        hint: `No SPF record at ${domain || "(no domain)"}. Publish one if you want SPF to count toward DMARC alignment.`,
      };
    default:
      return {
        kind: "unknown",
        severity: "info",
        label: result || "unknown",
        hint: `Unrecognised SPF result \`${result}\`.`,
      };
  }
}

// DKIM-specific. Same alignment rule against headerFrom.
export function diagnoseDkimAuth(
  row: { domain: string | null | undefined; selector?: string | null; result: string },
  headerFrom: string | undefined,
): AuthDiagnosis {
  const result = (row.result ?? "").toLowerCase();
  const domain = row.domain ?? "";
  const selectorTag = row.selector ? ` [selector ${row.selector}]` : "";

  switch (result) {
    case "pass": {
      const aligned = headerFrom && domain
        ? isSameOrganization(domain, headerFrom)
        : false;
      if (aligned) {
        return {
          kind: "aligned-pass",
          severity: "ok",
          label: "pass · aligned",
          hint: `DKIM signature on ${domain}${selectorTag} verified and aligns with the header From. Counts toward DMARC pass.`,
        };
      }
      return {
        kind: "unaligned-pass",
        severity: "warn",
        label: "pass · alignment failure",
        hint: `DKIM signature verified, but the signing domain (${domain}${selectorTag}) doesn't share an organizational domain with the header From (${headerFrom ?? "?"}). Usually means an ESP signed with its own d= (e.g. sendgrid.info) instead of yours. Configure the ESP to sign with d=${headerFrom ?? "<your-domain>"}.`,
      };
    }
    case "fail":
      return {
        kind: "auth-failure",
        severity: "error",
        label: "fail · signature invalid",
        hint: `DKIM signature didn't verify for ${domain}${selectorTag}. Either the message body was modified after signing (often a mailing list), the public key at the selector is wrong/missing, or the signing key changed mid-window.`,
      };
    case "neutral":
      return {
        kind: "neutral",
        severity: "info",
        label: "neutral",
        hint: `DKIM neutral at ${domain}${selectorTag}.`,
      };
    case "policy":
      return {
        kind: "unknown",
        severity: "warn",
        label: "policy",
        hint: `Receiver applied a local DKIM policy override for ${domain}${selectorTag}.`,
      };
    case "temperror":
      return {
        kind: "temperror",
        severity: "info",
        label: "temperror",
        hint: `Transient DNS error fetching the DKIM key for ${domain}${selectorTag}. Receiver-side hiccup — ignore unless persistent.`,
      };
    case "permerror":
      return {
        kind: "permerror",
        severity: "error",
        label: "permerror",
        hint: `Permanent DKIM error at ${domain}${selectorTag}. Usually a missing/malformed public key TXT at \`${row.selector ?? "<selector>"}._domainkey.${domain}\`.`,
      };
    case "none":
      return {
        kind: "none",
        severity: "warn",
        label: "unsigned",
        hint: `Message arrived without a DKIM signature for ${domain || "header From"}.`,
      };
    default:
      return {
        kind: "unknown",
        severity: "info",
        label: result || "unknown",
        hint: `Unrecognised DKIM result \`${result}\`.`,
      };
  }
}

// Record-level summary for the failure card header. Picks the most
// "informative" diagnosis to show at a glance:
//   1. Any SPF/DKIM alignment-failure-on-pass (the most common
//      surprise — auth passed but DMARC failed);
//   2. Any auth-failure (real config/spoofing signal);
//   3. Any permerror (config bug);
//   4. Any temperror;
//   5. Otherwise the first row.
//
// Returned as { source: "spf" | "dkim", diagnosis } so the UI can
// label which mechanism the summary refers to.
export function dominantFailureReason(
  spfRows: Array<{ domain: string | null | undefined; result: string }>,
  dkimRows: Array<{ domain: string | null | undefined; selector?: string | null; result: string }>,
  headerFrom: string | undefined,
): { source: "spf" | "dkim"; diagnosis: AuthDiagnosis } | null {
  const spfDx = spfRows.map((r) => ({ source: "spf" as const, diagnosis: diagnoseSpfAuth(r, headerFrom) }));
  const dkimDx = dkimRows.map((r) => ({ source: "dkim" as const, diagnosis: diagnoseDkimAuth(r, headerFrom) }));
  const all = [...spfDx, ...dkimDx];
  if (all.length === 0) return null;

  const priority: AuthDiagnosisKind[] = [
    "unaligned-pass",
    "auth-failure",
    "permerror",
    "softfail",
    "temperror",
    "none",
    "neutral",
    "unknown",
    "aligned-pass",
  ];
  for (const kind of priority) {
    const hit = all.find((d) => d.diagnosis.kind === kind);
    if (hit) return hit;
  }
  return all[0];
}
